<template>
  <div>
    <Header />
    <v-container style="max-width: 100%">
      <!-- OrderID Search Bar -->
      <v-row>
        <v-col cols="6">
          <v-card-text>
            <v-text-field v-model="searchInput" label="Bestellnummer" outlined @keydown.enter="doSearch()"></v-text-field>
          </v-card-text>
        </v-col>
        <v-col cols="6">
          <v-card-text>
            <v-text-field v-model="searchCustom" label="Gravurtext" outlined @keydown.enter="doSearchCustom()"></v-text-field>
          </v-card-text>
        </v-col>

        <v-col cols="12" v-if="error">
          <v-alert type="error">{{ error }}</v-alert>
        </v-col>
        <v-col cols="12">
          <v-btn block color="primary" dark @click="bulkReklaDialog = true">
            Massen-Reklamationen
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-if="order">
        <v-col cols="4">
          <v-card>
            <v-card-title primary-title> Adresse </v-card-title>
            <v-card-text>
              {{ order.customer.firstName }} {{ order.customer.lastName }}<br />
              {{ order.customer.adress }}<br />
              {{ order.customer.zip }} {{ order.customer.city }}<br />
            </v-card-text>
          </v-card>
          <v-card>
            <v-card-title primary-title>
              Status {{ order.managedId }} BillBee</v-card-title>
            <v-card-text>
              <v-chip v-if="order.shippingProfile && order.shippingProfile.backendName">{{ order.shippingProfile.backendName }}</v-chip>
              <v-chip>{{ order.state }}</v-chip>
              <v-chip v-if="order.plentyOrder">{{ order.plentyOrder.statusName }}</v-chip>
            </v-card-text>
          </v-card>
          <v-card>
            <v-card-title primary-title> Warenkorb </v-card-title>
            <v-card-text>
              <v-card class="mx-auto" outlined v-for="p of order.products" :key="order.products.indexOf(p)">
                <v-list-item three-line>
                  <v-list-item-content>
                    <div class="text-overline mb-4">Menge: {{ p.qty }} <span v-if="p.orderItemId">#{{ p.orderItemId }}</span></div>
                    <v-list-item-title class="text-h5 mb-1">
                      {{ p.sku }}
                    </v-list-item-title>
                    <v-list-item-subtitle v-if="p.attributes && Object.keys(p.attributes).length > 0
                    ">
                      <v-simple-table>
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left">Name</th>
                              <th class="text-left">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="a of Object.keys(p.attributes)" :key="a">
                              <td>{{ a }}</td>
                              <td>
                                <v-img v-if="p.attributes[a] && (p.attributes[a].toString().indexOf('.png') > -1 || p.attributes[a].toString().indexOf('.jpg') > -1)" :src="p.attributes[a]" width="100"  @click="openImage(p.attributes[a])"/>
                                <strong v-else>{{ p.attributes[a] }}</strong>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-card>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="5">
          <v-card v-if="order.plentyOrder && order.plentyOrder.shippingPackages.length > 0">
            <v-card-title primary-title> Paketnummer </v-card-title>
            <v-card-text>
              <v-alert dense outlined text v-for="s of order.plentyOrder.shippingPackages" :key="s.id">
                {{ s.packageNumber }}
              </v-alert>
            </v-card-text>
          </v-card>
          <v-card v-if="order.retoure.length > 0">
            <v-card-title primary-title> Retoure </v-card-title>
            <v-card-text>
              <v-alert dense outlined text type="warning">
                Das Paket befindet sich in der Retourenabteilung.
              </v-alert>

              <v-simple-table v-for="retoure of order.retoure" :key="retoure.date">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Name</th>
                      <th class="text-left">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Datum</td>
                      <td>{{ new Date(retoure.date).toLocaleString() }}</td>
                    </tr>
                    <tr>
                      <td>Typ</td>
                      <td>{{ retoure.type }}</td>
                    </tr>
                    <tr>
                      <td>Grund</td>
                      <td>{{ retoure.reason }}</td>
                    </tr>
                    <tr>
                      <td>Neu Versand</td>
                      <td>{{ retoure.reshipment ? retoure.reshipment : '-' }}</td>
                    </tr>
                    <tr>
                      <td>Entsorgt</td>
                      <td>{{ retoure.trash == 0 ? 'NEIN' : 'JA' }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>

          <v-card :loading="loading">
            <v-text-field v-model="order.tracking" label="Tracking Nummer" outlined></v-text-field>
            
            <!-- Meilensteine Timeline -->
            <v-card v-if="milestones && milestones.length > 0" class="mb-4" elevation="2">
              <v-card-title class="primary--text">
                <v-icon left color="primary">mdi-timeline-text</v-icon>
                Bestellverlauf
              </v-card-title>
              <v-card-text>
                <!-- Desktop Timeline -->
                <v-timeline align-top dense class="d-none d-md-block">
                  <!-- Meilensteine -->
                  <v-timeline-item
                    v-for="(milestone, index) in milestones"
                    :key="index"
                    :color="getTimelineColor(milestone.type)"
                    :icon="getTimelineIcon(milestone.type)"
                    fill-dot
                  >
                    <v-card class="elevation-2">
                      <v-card-title class="text-h6 pb-2">
                        <v-row no-gutters align="center">
                          <v-col>
                            {{ getTimelineTitle(milestone.type) }}
                          </v-col>
                          <v-col cols="auto">
                            <v-chip small :color="getTimelineColor(milestone.type)" dark>
                              {{ formatMilestoneDate(milestone.date) }}
                            </v-chip>
                          </v-col>
                        </v-row>
                      </v-card-title>
                      <v-card-text class="pt-0">
                        <div class="text-body-1 mb-1">{{ milestone.description }}</div>
                        <div class="d-flex align-center text-caption grey--text">
                          <v-icon x-small class="mr-1">mdi-calendar-clock</v-icon>
                          {{ formatMilestoneDateTime(milestone.date) }}
                          <template v-if="getDaysSinceStart(milestone.date, index)">
                            <v-divider vertical class="mx-2" />
                            <v-chip x-small outlined :color="getTimelineColor(milestone.type)">
                              <v-icon x-small left>mdi-timer-outline</v-icon>
                              Nach {{ getDaysSinceStart(milestone.date, index) }}
                            </v-chip>
                          </template>
                          <template v-if="milestone.user">
                            <v-divider vertical class="mx-2" />
                            <v-icon x-small class="mr-1">mdi-account</v-icon>
                            {{ milestone.user }}
                          </template>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-timeline-item>
                  
                  <!-- Tracking Steps -->
                  <template v-if="tracking && ((tracking.events && tracking.events.length > 0) || (tracking.steps && tracking.steps.length > 0))">
                    <v-timeline-item
                      v-for="(event, index) in (tracking.steps || tracking.events)"
                      :key="`tracking-${index}`"
                      :color="getTrackingEventColor(event)"
                      :icon="getTrackingEventIcon(event)"
                      v-if="event.statusCode && event.statusCode.toUpperCase() != 'PRE-TRANSIT'"
                      fill-dot
                    >
                      <v-card class="elevation-1">
                        <v-card-title class="text-subtitle-1 pb-2">
                          <v-row no-gutters align="center">
                            <v-col>
                              <v-icon small class="mr-2">mdi-truck-delivery-outline</v-icon>
                              {{ event.description || event.status || '' }}
                            </v-col>
                            <v-col cols="auto">
                              <v-chip small color="grey" dark>
                                {{ formatTrackingDate(event.timestamp || event.date) }}
                              </v-chip>
                            </v-col>
                          </v-row>
                        </v-card-title>
                        <v-card-text class="pt-0">
                          <div class="text-body-2 mb-1">
                            <template v-if="event.location">
                              <v-icon small>mdi-map-marker</v-icon>
                              {{ event.location ? getLocationString(event.location) : event.place }}
                            </template>
                            <template v-else-if="event.place">
                              <v-icon small>mdi-map-marker</v-icon>
                              {{ event.place }}
                            </template>
                          </div>
                          <div v-if="event.remark && event.remark !== event.description" class="text-caption grey--text mb-1">
                            {{ event.remark }}
                          </div>
                          <div class="text-caption grey--text">
                            <v-icon x-small>mdi-clock-outline</v-icon>
                            {{ formatTrackingDate(event.timestamp || event.date) }}
                            <v-chip x-small outlined color="grey" class="ml-2" v-if="getDaysSinceFirstMilestone(event.timestamp || event.date)">
                              <v-icon x-small left>mdi-timer-outline</v-icon>
                              Nach {{ getDaysSinceFirstMilestone(event.timestamp || event.date) }}
                            </v-chip>
                          </div>
                          <div v-if="event.statusCode" class="mt-1">
                            <v-chip x-small outlined>
                              {{ event.statusCode.toUpperCase() }}
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-timeline-item>
                  </template>
                </v-timeline>
                
                <!-- Mobile Timeline -->
                <div class="d-md-none">
                  <!-- Meilensteine -->
                  <v-card
                    v-for="(milestone, index) in milestones"
                    :key="`mobile-${index}`"
                    class="mb-3"
                    outlined
                  >
                    <v-card-text>
                      <div class="d-flex align-center mb-2">
                        <v-avatar
                          size="32"
                          :color="getTimelineColor(milestone.type)"
                          class="mr-3"
                        >
                          <v-icon small dark>{{ getTimelineIcon(milestone.type) }}</v-icon>
                        </v-avatar>
                        <div class="flex-grow-1">
                          <div class="font-weight-bold">{{ getTimelineTitle(milestone.type) }}</div>
                          <div class="text-caption">{{ formatMilestoneDate(milestone.date) }}</div>
                        </div>
                      </div>
                      <div class="text-body-2 mb-1">{{ milestone.description }}</div>
                      <div class="text-caption grey--text">
                        <v-icon x-small>mdi-clock-outline</v-icon>
                        {{ formatMilestoneTime(milestone.date) }}
                        <span v-if="getDaysSinceStart(milestone.date, index)" class="ml-2">
                          • Nach {{ getDaysSinceStart(milestone.date, index) }}
                        </span>
                        <span v-if="milestone.user" class="ml-2">
                          • {{ milestone.user }}
                        </span>
                      </div>
                    </v-card-text>
                  </v-card>
                  
                  <!-- Tracking Steps Mobile -->
                  <template v-if="tracking && ((tracking.events && tracking.events.length > 0) || (tracking.steps && tracking.steps.length > 0))">
                    <v-divider class="my-3" />
                    <div class="text-subtitle-2 grey--text mb-2">
                      <v-icon small>mdi-truck-delivery</v-icon>
                      Sendungsverlauf
                    </div>
                    <v-card
                      v-for="(event, index) in (tracking.steps || tracking.events)"
                      :key="`mobile-tracking-${index}`"
                      class="mb-2"
                      outlined
                      :style="`border-left-color: ${getTrackingEventColor(event)}`"
                    >
                      <v-card-text class="pa-3">
                        <div class="d-flex align-center mb-1">
                          <v-icon small :color="getTrackingEventColor(event)" class="mr-2">
                            {{ getTrackingEventIcon(event) }}
                          </v-icon>
                          <div class="font-weight-medium text-body-2">
                            {{ event.description || event.status || '' }}
                          </div>
                        </div>
                        <div class="text-caption grey--text">
                          <div v-if="event.location || event.place">
                            <v-icon x-small>mdi-map-marker</v-icon>
                            {{ event.location ? getLocationString(event.location) : event.place }}
                          </div>
                          <div v-if="event.remark && event.remark !== event.description">
                            {{ event.remark }}
                          </div>
                          <div>
                            <v-icon x-small>mdi-clock-outline</v-icon>
                            {{ formatTrackingDate(event.timestamp || event.date) }}
                            <span v-if="getDaysSinceFirstMilestone(event.timestamp || event.date)" class="ml-2">
                              • Nach {{ getDaysSinceFirstMilestone(event.timestamp || event.date) }}
                            </span>
                          </div>
                          <div v-if="event.statusCode" class="mt-1">
                            <v-chip x-small outlined>
                              {{ event.statusCode.toUpperCase() }}
                            </v-chip>
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </template>
                </div>
              </v-card-text>
            </v-card>

            <v-card-title primary-title> Tracking Verlauf </v-card-title>
            <v-card-text v-if="tracking">
              <DHLTracking v-if="tracking.carrier == 'dhl'" :tracking="tracking" />
              <DPDTracking v-if="tracking.carrier == 'dpd'" :tracking="tracking" />
            </v-card-text>
            <v-alert v-else type="warning" :value="true">
              Kein Tracking verfügbar.
            </v-alert>
          </v-card>
        </v-col>
        <v-col cols="3">
          <v-card>
            <v-card-title primary-title> Aktionen </v-card-title>
            <v-card-text>
              <v-alert
                v-if="isOldAmazonOrder"
                type="warning"
                dense
                text
              >
                Achtung: Diese Amazon-Bestellung ist älter als 40 Tage. Die Kundendaten wurden möglicherweise bereits anonymisiert.
              </v-alert>
              <v-btn block color="primary" dark @click="loadReklaDialog()">Reklamation einleiten</v-btn>
              <v-btn block color="primary" dark @click="resetOrder()">Zurücksetzen</v-btn>
              <v-btn block color="success" dark @click="setPacked()">Gepackt setzen</v-btn>
              <v-btn 
                block 
                color="primary" 
                dark 
                @click="openPrinterDialog(order.billbeeOrder.Data.ShippingIds ? order.billbeeOrder.Data.ShippingIds[0] : null)"
                v-if="order && order.billbeeOrder && order.billbeeOrder.Data"
              >
                <v-icon left>mdi-printer</v-icon>
                Versandschein drucken
              </v-btn>
            </v-card-text>
          </v-card>
          <v-card v-if="order.claims">
            <v-card-title primary-title> Bestehende Reklamationen </v-card-title>
            <v-card-text>
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">ERP ID</th>
                      <th class="text-left">Bestellnummer</th>
                      <th class="text-left">Datum</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="claim of order.claims" :key="claim.id">
                      <td>{{ claim.managedId ? claim.managedId : claim.plentyId }}</td>
                      <td>{{ claim.orderId }}</td>
                      <td>{{ new Date(claim.date).toLocaleDateString() }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>


        </v-col>
      </v-row>


      <v-row v-if="orders">

        <v-col cols="12" v-for="o of orders" :key="o._id">

          <v-card>

            <v-card-title primary-title> {{ o.orderId }} | {{ o.managedId }} </v-card-title>
            <v-card-subtitle>Vom {{ o.createDate | moment("DD.MM.yyyy HH:mm") }} </v-card-subtitle>

            <v-card-text>
              <v-alert type="warning" v-if="o.printDate">Achtung! Das Label wurde bereits gedruckt. Erstelle ein neues Label, um ggf. etwas Nachzusenden. Nicht das Label 2x Verwenden!</v-alert>
              <v-row>
                <v-col v-if="o.production && o.production.laserEnd">
                  <h3>Graviert</h3>
                  <p> Am {{ o.production.laserEnd | moment("DD.MM.yyyy HH:mm") }}</p>
                  <p>Von <strong>{{ o.production.userLaser }}</strong></p>
                </v-col>
                <v-col v-if="o.production && o.production.packEnd">
                  <h3>Gepackt</h3>
                  <p> Am {{ o.production.packEnd | moment("DD.MM.yyyy HH:mm") }}</p>
                  <p>Von <strong>{{ o.production.userPacking }}</strong></p>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <h2>Paket Inhalt</h2>
                </v-col>
                <v-col cols="12">

                  <v-data-table :headers="tableHeaders" :items="o.products" :items-per-page="150" class="elevation-1">
                    <template v-slot:item.attributes="{ item }">
                      <ul>
                        <li v-for="t of Object.keys(item.attributes)" :v-key="t"> {{ t }}: <strong>{{ item.attributes[t] }}</strong></li>
                      </ul>
                    </template>


                  </v-data-table>

                </v-col>
              </v-row>
            </v-card-text>

          </v-card>


        </v-col>

      </v-row>
    </v-container>
    <!-- Reklamations Dialog -->
    <v-dialog v-model="reklaDialog" persistent max-width="1000">
      <v-card :loading="loading">
        <v-card-title class="headline">Reklamation erstellen</v-card-title>
        <v-card-text>
          <v-form ref="form">
            <v-row>
              <v-col cols="12" md="12">
                <v-card outlined class="mb-4">
                  <v-card-subtitle class="py-2 primary--text font-weight-bold">
                    Reklamationsdetails
                  </v-card-subtitle>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" md="12">
                        <v-textarea 
                          v-model="reklaData.note"
                          label="Was soll reklamiert werden?"
                          outlined
                          auto-grow
                          rows="2"
                          counter
                          required
                        ></v-textarea>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-autocomplete
                          v-model="reklaData.reason"
                          :items="reasons"
                          label="Reklamationsgrund"
                          outlined
                          dense
                          required
                        ></v-autocomplete>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="reklaData.type"
                          :items="['WIR', 'DPD', 'POST']"
                          label="Schuld"
                          outlined
                          dense
                          required
                        ></v-select>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-expansion-panels v-model="openPanels" multiple>
              <!-- Billbee Artikel Panel -->
              <v-expansion-panel>
                <v-expansion-panel-header class="primary--text">
                  <v-row no-gutters>
                    <v-col cols="10">Billbee Artikel auswählen</v-col>
                    <v-col cols="2" class="text-right">
                      <v-chip
                        small
                        outlined
                        color="primary"
                        v-if="reklaItems.length > 0"
                      >
                        {{ reklaItems.length }} ausgewählt
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-data-table 
                    v-if="order && order.billbeeOrder" 
                    show-select 
                    :headers="reklaTableHeaders" 
                    :items="order.billbeeOrder.Data ? order.billbeeOrder.Data.OrderItems : order.billbeeOrder.OrderItems ? order.billbeeOrder.OrderItems : []" 
                    item-key="BillbeeId" 
                    v-model="reklaItems"
                    :items-per-page="-1"
                    dense
                  >
                    <template v-slot:item.Product.Title="{ item }">
                      {{ item.Product.Title }}
                    </template>
                    <template v-slot:item.Attributes="{ item }">
                      <ul v-if="item.Attributes">
                        <li v-for="attr in item.Attributes" :key="attr.Id">
                          {{ attr.Name }}: <strong>{{ attr.Value }}</strong>
                        </li>
                      </ul>
                    </template>
                    <template v-slot:item.Quantity="{ item }">
                      {{ item.Quantity }}
                    </template>
                    <template v-slot:item.TotalPrice="{ item }">
                      {{ item.TotalPrice }} €
                    </template>
                  </v-data-table>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- Unsere Artikel Panel -->
              <v-expansion-panel>
                <v-expansion-panel-header class="primary--text">
                  <v-row no-gutters>
                    <v-col cols="10">Unsere Artikel auswählen</v-col>
                    <v-col cols="2" class="text-right">
                      <v-chip
                        small
                        outlined
                        color="primary"
                        v-if="reklaData.mongoItems.length > 0"
                      >
                        {{ reklaData.mongoItems.length }} ausgewählt
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-data-table 
                    v-if="order && order.products" 
                    show-select 
                    :headers="tableHeaders" 
                    :items="order.products" 
                    item-key="orderItemId" 
                    v-model="reklaData.mongoItems"
                    :items-per-page="-1"
                    dense
                  >
                    <template v-slot:item.attributes="{ item }">
                      <ul v-if="item.attributes">
                        <li v-for="attr in Object.keys(item.attributes)" :key="attr">
                          {{ attr }}: <strong>{{ item.attributes[attr] }}</strong>
                        </li>
                      </ul>
                    </template>
                  </v-data-table>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- Ersatzteile Panel -->
              <v-expansion-panel>
                <v-expansion-panel-header class="primary--text">
                  <v-row no-gutters>
                    <v-col cols="10">Ersatzteile hinzufügen</v-col>
                    <v-col cols="2" class="text-right">
                      <v-chip
                        small
                        outlined
                        color="primary"
                        v-if="selectedSpareParts.length > 0"
                      >
                        {{ selectedSpareParts.length }} ausgewählt
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card flat>
                    <v-card-text class="pa-0">
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-autocomplete
                            v-model="selectedSparePart"
                            :items="spareParts"
                            label="Ersatzteil auswählen"
                            item-text="name"
                            item-value="sku"
                            return-object
                            outlined
                            dense
                            clearable
                          ></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model.number="sparePartQuantity"
                            label="Menge"
                            type="number"
                            min="1"
                            outlined
                            dense
                            :rules="[v => v > 0 || 'Menge muss mindestens 1 sein']"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="3" class="d-flex align-center">
                          <v-btn 
                            color="primary" 
                            @click="addSparePart" 
                            :disabled="!selectedSparePart"
                            block
                          >
                            <v-icon left>mdi-plus</v-icon>
                            Hinzufügen
                          </v-btn>
                        </v-col>
                      </v-row>

                      <!-- Ausgewählte Ersatzteile -->
                      <v-data-table
                        v-if="selectedSpareParts.length > 0"
                        :headers="sparePartsHeaders"
                        :items="selectedSpareParts"
                        class="mt-4"
                        dense
                        :items-per-page="5"
                      >
                        <template v-slot:item.actions="{ item }">
                          <v-btn
                            small
                            icon
                            color="error"
                            @click="removeSparePart(item)"
                          >
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                      </v-data-table>
                      
                      <v-alert
                        v-else
                        type="info"
                        text
                        dense
                        class="mt-4"
                      >
                        Keine Ersatzteile ausgewählt. Wählen Sie Ersatzteile aus, die Sie dem Kunden zusenden möchten.
                      </v-alert>
                    </v-card-text>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="reklaDialog = false">
            Abbrechen
          </v-btn>
          <v-btn 
            color="primary" 
            text 
            @click="createRekla()"
            :disabled="!reklaData.reason || !reklaData.type || !reklaData.note"
          >
            Erzeugen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Warnungs Dialog für alte Amazon Bestellungen -->
    <v-dialog v-model="warningDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Achtung!</v-card-title>
        <v-card-text>
          Diese Amazon-Bestellung ist älter als 40 Tage. Die Kundendaten wurden möglicherweise bereits anonymisiert. 
          Möchten Sie trotzdem fortfahren?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="warningDialog = false">
            Abbrechen
          </v-btn>
          <v-btn 
            color="primary" 
            text 
            @click="() => {
              warningDialog = false;
              reklaData.managedId = order.managedId;
              reklaData.orderId = order.orderId;
              reklaData.reason = '';
              reklaData.note = 'Beschädigt';
              reklaData.type = '';
              reklaDialog = true;
            }"
          >
            Fortfahren
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Nach dem bestehenden rekla-Dialog -->
    <v-dialog v-model="bulkReklaDialog" persistent max-width="1000">
      <v-card :loading="processingOrders">
        <v-card-title class="headline">Massen-Reklamationen erstellen</v-card-title>
        <v-card-text>
          <v-form ref="bulkForm">
            <v-row>
              <v-col cols="12" md="12">
                <v-card outlined class="mb-4">
                  <v-card-subtitle class="py-2 primary--text font-weight-bold">
                    Reklamationsdetails
                  </v-card-subtitle>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12">
                        <v-textarea
                          v-model="bulkOrderIds"
                          label="Bestellnummern (eine pro Zeile)"
                          required
                          rows="4"
                          outlined
                          auto-grow
                          counter
                        ></v-textarea>
                      </v-col>
                      <v-col cols="12">
                        <v-textarea 
                          v-model="bulkReklaData.note"
                          label="Was soll reklamiert werden?"
                          required
                          rows="2"
                          outlined
                          auto-grow
                          counter
                        ></v-textarea>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-autocomplete
                          v-model="bulkReklaData.reason"
                          :items="reasons"
                          label="Reklamationsgrund"
                          required
                          outlined
                          dense
                        ></v-autocomplete>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="bulkReklaData.type"
                          :items="['WIR', 'DPD', 'POST']"
                          label="Schuld"
                          required
                          outlined
                          dense
                        ></v-select>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Ersatzteile für Bulk-Reklamationen -->
            <v-card outlined class="mb-4">
              <v-card-subtitle class="py-2 primary--text font-weight-bold">
                Ersatzteile hinzufügen
                <v-chip
                  small
                  outlined
                  color="primary"
                  class="ml-2"
                  v-if="bulkSelectedSpareParts.length > 0"
                >
                  {{ bulkSelectedSpareParts.length }} ausgewählt
                </v-chip>
              </v-card-subtitle>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="selectedBulkSparePart"
                      :items="spareParts"
                      label="Ersatzteil auswählen"
                      item-text="name"
                      item-value="sku"
                      return-object
                      outlined
                      dense
                      clearable
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model.number="bulkSparePartQuantity"
                      label="Menge"
                      type="number"
                      min="1"
                      outlined
                      dense
                      :rules="[v => v > 0 || 'Menge muss mindestens 1 sein']"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3" class="d-flex align-center">
                    <v-btn 
                      color="primary" 
                      @click="addBulkSparePart" 
                      :disabled="!selectedBulkSparePart"
                      block
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Hinzufügen
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- Ausgewählte Ersatzteile für Bulk-Reklamationen -->
                <v-data-table
                  v-if="bulkSelectedSpareParts.length > 0"
                  :headers="sparePartsHeaders"
                  :items="bulkSelectedSpareParts"
                  class="mt-4"
                  dense
                  :items-per-page="5"
                >
                  <template v-slot:item.actions="{ item }">
                    <v-btn
                      small
                      icon
                      color="error"
                      @click="removeBulkSparePart(item)"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
                
                <v-alert
                  v-else
                  type="info"
                  text
                  dense
                  class="mt-4"
                >
                  Keine Ersatzteile ausgewählt. Wählen Sie Ersatzteile aus, die Sie dem Kunden zusenden möchten.
                </v-alert>
              </v-card-text>
            </v-card>

            <v-progress-linear
              v-if="processingOrders"
              :value="(progressValue / progressTotal) * 100"
              color="primary"
              class="mb-4"
            >
              {{ progressValue }} / {{ progressTotal }}
            </v-progress-linear>

            <v-card outlined class="mb-4" v-if="processedResults.length > 0">
              <v-card-subtitle class="py-2 primary--text font-weight-bold">
                Verarbeitete Bestellungen
              </v-card-subtitle>
              <v-card-text class="pa-0">
                <v-list dense>
                  <v-list-item
                    v-for="result in processedResults"
                    :key="result.orderId"
                    :class="result.success ? 'success--text' : 'error--text'"
                  >
                    <v-list-item-icon>
                      <v-icon :color="result.success ? 'success' : 'error'">
                        {{ result.success ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ result.orderId }}</v-list-item-title>
                      <v-list-item-subtitle>{{ result.message }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>

            <v-textarea
              v-if="failedOrderIds"
              v-model="failedOrderIds"
              label="Fehlgeschlagene Bestellnummern"
              readonly
              rows="3"
              class="mt-4"
              outlined
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="bulkReklaDialog = false"
            :disabled="processingOrders"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="primary"
            text
            @click="processBulkReklamationen"
            :disabled="processingOrders || !bulkOrderIds || !bulkReklaData.reason || !bulkReklaData.type"
          >
            <v-icon left v-if="!processingOrders">mdi-check</v-icon>
            <v-progress-circular
              indeterminate
              size="20"
              width="2"
              v-else
              class="mr-2"
            ></v-progress-circular>
            Reklamationen erstellen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <LoadingDialog />
    <v-dialog v-model="printerDialog" max-width="500px">
      <v-card>
        <v-card-title class="primary--text">
          <v-icon left color="primary">mdi-printer</v-icon>
          Drucker auswählen
        </v-card-title>
        <v-card-text>
          <v-alert
            type="info"
            text
            dense
            class="mb-4"
          >
            Wählen Sie einen Drucker aus, um den Versandschein zu drucken.
          </v-alert>
          <v-select
            v-model="selectedPrinter"
            :items="printers"
            label="Drucker"
            item-text="name"
            item-value="id"
            outlined
            dense
            prepend-icon="mdi-printer-settings"
            required
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="printerDialog = false">
            Abbrechen
          </v-btn>
          <v-btn 
            color="primary" 
            @click="printShippingLabel"
            :disabled="!selectedPrinter"
            :loading="printingLabel"
          >
            <v-icon left>mdi-printer</v-icon>
            Drucken
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Header from "./header";
import service from "@/services/ProductionService";
import customerSupportService from "@/services/CustomerSupportService";
import router from "@/router";

import DHLTracking from "@/components/DHLTracking";
import DPDTracking from "@/components/DPDTracking";

import LoadingDialog from "./production/components/LoadingDialog";

import fileDownload from "js-file-download";
import moment from "moment";

import EventBus from "@/event-bus";

import { OrderBuilder } from '@/js/BillbeeOrderBuilder.js';
import { ref } from 'vue';

export default {
  name: "dashboard",
  components: {
    Header,
    DHLTracking,
    DPDTracking,
    LoadingDialog,
  },
  props: {
    search: {
      type: String,
      default: null
    }
  },
  data: () => ({
    timer: null,
    loading: false,
    searchInput: null,
    order: null,
    tracking: null,
    searchCustom: null,
    orders: null,
    reklaDialog: false,
    reklaItems: [],
    reasons: [
      "Fehler",
      "Beschädigte Ware",
      "Beschädigte Verpackung",
      "Fehlender Bestandteil",
      "Funktioniert Nicht",
      "Falsche Farbe",
      "Falsche Gravur",
      "Falscher Druck",
      "Verpäteter Versand",
      "Verlust der Sendung",
      "Verspaetete Lieferung",
      "Angebliche Zustellung"
    ],
    reklaData: {
      orderId: "",
      managedId: "",
      reason: "",
      note: "",
      type: "",
      mongoItems: []
    },
    tableHeaders: [
      { text: "SKU", value: "sku" },
      { text: "Menge", value: "qty" },
      { text: "Gravur", value: "attributes" },
    ],

    reklaTableHeaders: [
      { text: "Artikel", value: "Product.Title" },
      { text: "Attribute", value: "Attributes" },
      { text: "Menge", value: "Quantity" },
      { text: "Preis", value: "TotalPrice" }
    ],
    warningDialog: false,
    bulkReklaDialog: false,
    bulkOrderIds: '',
    bulkReklaData: {
      reason: '',
      note: '',
      type: ''
    },
    processingOrders: false,
    processedResults: [],
    progressValue: 0,
    progressTotal: 0,
    failedOrderIds: '',
    printerDialog: false,
    selectedPrinter: null,
    printers: [],
    currentShippingId: null,
    selectedSparePart: null,
    sparePartQuantity: 1,
    selectedSpareParts: [],
    sparePartsHeaders: [
      { text: "Artikel", value: "name" },
      { text: "SKU", value: "sku" },
      { text: "Menge", value: "quantity" },
      { text: "Aktionen", value: "actions", sortable: false }
    ],
    spareParts: [
      { name: "Spardosen Schlüssel", sku: "SPARDOSE-SCHLUESSEL", price: 0 },
      { name: "Spardosen Schloss", sku: "SPARDOSE_SCHLOSS", price: 0 },
      { name: "Herzschloss Schlüssel silber", sku: "HEARTLOCK-KEY-SILVER", price: 0 },
      { name: "Herzschloss Schlüssel gold", sku: "HEARTLOCK-KEY-GOLD", price: 0 },
      { name: "Herzschloss Schlüssel rose", sku: "HEARTLOCK-KEY-ROSE", price: 0 },
      { name: "Liebesschloss (TL) Schlüssel Silber", sku: "LOVELOCK-KEY-SILVER", price: 0 },
      { name: "Liebesschloss (TL) Schlüssel Gold", sku: "LOVELOCK-KEY-GOLD", price: 0 },
      { name: "Liebesschloss (TL) Schlüssel Rose", sku: "LOVELOCK-KEY-ROSE", price: 0 },
      { name: "Uhrwerk", sku: "UHRWERK", price: 0 },
      { name: "Wandhalterung Messlatte", sku: "WANDHALTERUNG-MESSLATTE", price: 0 },
      { name: "Kleiderhaken Garderobe", sku: "KLEIDERHAKEN-GARDEROBEN", price: 0 },
      { name: "Wandhalterung Tonie", sku: "TONIE-WALL-HOLDER", price: 0 },
      { name: "Magnetboden klein Tonie", sku: "TONIE-MAGNETFUSS-KLEIN", price: 0 },
      { name: "Magnetboden gross Tonie", sku: "TONIE-MAGNETFUSS-GROSS", price: 0 },
      { name: "LED Sockel", sku: "LED-SOCKEL", price: 0 },
      { name: "RGB Sockel", sku: "RGB-SOCKEL", price: 0 },
      { name: "Stift Stundenplan", sku: "STIFT-STUNDENPLAN", price: 0 },
      { name: "Sockel Stundenplan", sku: "SOCKEL", price: 0 },
      { name: "Netzteil", sku: "NETZTEIL", price: 0 },
      { name: "USB Kabel", sku: "USB-KABEL", price: 0 },
      { name: "Zippo Geschenkverpackung", sku: "ZIPPO-GESCHENKVERPACKUNG", price: 0 },
      { name: "Schnapsglas", sku: "SCHNAPSGLAS", price: 0 },
      { name: "Besteckverpackung Tiere", sku: "BESTECK-VERPACKUNG-TIERE", price: 0 },
      { name: "Besteckverpackung Safari", sku: "BESTECK-VERPACKUNG-SAFARI", price: 0 },
      { name: "Besteckverpackung Farm", sku: "BESTECK-VERPACKUNG-FARM", price: 0 },
      { name: "Besteckverpackung Einhorn", sku: "BESTECK-VERPACKUNG-EINHORN", price: 0 },
      { name: "Besteckverpackung Lyric", sku: "BESTECK-VERPACKUNG-LYRIC", price: 0 },
      { name: "Besteckverpackung Knuddel", sku: "BESTECK-VERPACKUNG-KNUDDEL", price: 0 },
      { name: "Besteckverpackung Zwerge", sku: "BESTECK-VERPACKUNG-ZWERGE", price: 0 },
      { name: "Besteckverpackung Bauernfreunde", sku: "BESTECK-VERPACKUNG-BAUERNFREUNDE", price: 0 },
      { name: "Besteckverpackung Waldfreunde", sku: "BESTECK-VERPACKUNG-WALDFREUNDE", price: 0 },
      { name: "Besteckverpackung Zoo", sku: "BESTECK-VERPACKUNG-ZOO", price: 0 },
      { name: "Besteckverpackung Prinzessin", sku: "BESTECK-VERPACKUNG-PRINZESSIN", price: 0 },
      { name: "Besteckverpackung Fussballfreunde", sku: "BESTECK-VERPACKUNG-FUSSBALLFREUNDE", price: 0 },
      { name: "Teller Safari", sku: "TELLER-SAFARI", price: 0 },
      { name: "Teller Janosch", sku: "TELLER-JANOSCH", price: 0 },
      { name: "Schüssel Janosch", sku: "SCHUESSEL-JANOSCH", price: 0 },
      { name: "Schüssel Safari", sku: "SCHUESSEL-SAFARI", price: 0 },
      { name: "Geschenkverpackung Herzschloss", sku: "GESCHENKVERPACKUNG-HERZSCHLOSS", price: 0 },
      { name: "Geschenkverpackung Liebesschloss", sku: "GESCHENKVERPACKUNG-LIEBESCHLOSS", price: 0 },
    ],
    selectedBulkSparePart: null,
    bulkSparePartQuantity: 1,
    bulkSelectedSpareParts: [],
    openPanels: [0, 1, 2], // Alle Panels standardmäßig geöffnet
    printingLabel: false,
    milestones: [], // Neue Variable für Meilensteine
  }),
  create() { },
  async mounted() {
    this.loadPrinters();
    this.loadReasons();
    
    // Check for search query parameter and auto-search
    if (this.search) {
      this.searchInput = this.search;
      // Auto-search after a short delay to ensure component is fully mounted
      setTimeout(() => {
        this.doSearch();
      }, 100);
    }
  },
  methods: {
    async doSearch() {
      try {
        EventBus.$emit("LOADING_START");
        this.tracking = null;
        this.milestones = []; // Reset milestones
        let order = (await service.getMongoOrder(this.searchInput)).data;

        //set orderItemIds
        for (let item of order.products) {
          item.orderItemId = order.products.indexOf(item);
        }

        if (!order.managedId && order.billbeeOrder) {
          order.managedId = order.billbeeOrder.Data.BillBeeOrderId;
        }

        this.order = order;
        EventBus.$emit("LOADING_END");
        if (this.order) {
          this.getTracking();
          this.getMilestones();
          //this.loadClaimCreationInformations();

        }
      } catch (err) {
        this.error = "Bestellung konnte nicht gefunden werden!";
        EventBus.$emit("LOADING_END");
        setTimeout(function () {
          this.error = null;
        }, 3500);
      }
    },
    async doSearchCustom() {
      try {
        EventBus.$emit("LOADING_START");
        this.order = null;
        this.orders = [];

        this.orders = (await service.searchCustom(this.searchCustom)).data;

        EventBus.$emit("LOADING_END");
      } catch (err) {
        this.error = "Bestellung konnte nicht gefunden werden!";
        EventBus.$emit("LOADING_END");
        setTimeout(function () {
          this.error = null;
        }, 3500);
      }
    },

    async getTracking() {
      this.loading = true;
      try {
        if (this.order.tracking) {
          this.tracking = (
            await service.getTrackingState(this.order.tracking)
          ).data;
          this.tracking.steps.reverse();
          this.tracking.events.reverse();
        }
        this.loading = false;
      } catch (err) { }
    },

    async resetOrder() {
      //this method should post a request to server to reset the order
      this.loading = true;
      let result = await service.resetOrder(this.order.orderId);
      if(result.status == 200) {
        alert("Bestellung zurückgesetzt");
      } else {
        alert("Bestellung konnte nicht zurückgesetzt werden");
      }
      this.loading = false;
    },

    async setPacked() {
      this.loading = true;
      try {
        let result = await customerSupportService.setPacked(this.order.orderId);
        if (result.status == 200) {
          alert("Bestellung als gepackt gesetzt");
        } else {
          alert("Bestellung konnte nicht gesetzt werden");
        }
      } catch (err) {
        alert("Bestellung konnte nicht gesetzt werden");
      }
      this.loading = false;
    },

    loadReklaDialog() {
      if (this.order.billbeeOrder && 
          (this.order.billbeeOrder.Data ? this.order.billbeeOrder.Data.ApiAccountName.toLowerCase().includes('amazon') : this.order.billbeeOrder.ApiAccountName.toLowerCase().includes('amazon'))) {
        
        const orderDate = new Date(this.order.billbeeOrder.Data.CreatedAt);
        const daysDiff = Math.floor((new Date() - orderDate) / (1000 * 60 * 60 * 24));
        
        if (daysDiff > 40) {
          this.warningDialog = true;
          return;
        }
      }

      this.reklaData.managedId = this.order.managedId;
      this.reklaData.orderId = this.order.orderId;
      this.reklaData.reason = "";
      this.reklaData.note = "";
      this.reklaData.type = "";
      
      // Alle Einträge im MongoDB-Datatable auswählen
      this.reklaData.mongoItems = [...this.order.products];
      
      // Alle Einträge im Billbee-Datatable auswählen
      if (this.order.billbeeOrder && this.order.billbeeOrder.Data) {
        this.reklaItems = [...this.order.billbeeOrder.Data.OrderItems];
      } else if (this.order.billbeeOrder && this.order.billbeeOrder.OrderItems) {
        this.reklaItems = [...this.order.billbeeOrder.OrderItems];
      }
      
      this.reklaDialog = true;
    },


    async createRekla() {
      this.loading = true;
      try {
        // Update MongoDB-Items mit Ersatzteilen
        this.updateMongoItemsWithSpareParts();
        
        // Erstelle neue Billbee Order aus den ausgewählten Items
        const newOrder = this.createBillbeeOrderFromItems();

        console.log(newOrder);

        const response = await service.createRekla({
          originalOrder: this.order.billbeeOrder.Data ? this.order.billbeeOrder.Data : this.order.billbeeOrder,
          warrantyOrder: newOrder,
          mongoItems: this.reklaData.mongoItems,
          note: this.reklaData.note,
          reason: this.reklaData.reason,
          type: this.reklaData.type
        });

        this.reklaDialog = false;
        this.reklaItems = [];
        this.reklaData = {
          orderId: "",
          managedId: "",
          reason: "",
          note: "",
          type: "",
          mongoItems: []
        };
        this.selectedSpareParts = [];
      } catch (err) {
        console.error(err);
        this.error = "Es konnte keine Reklamation erzeugt werden!";
      } finally {
        this.loading = false;
      }
    },

    getNextClaimPrefix() {
      // Prüfe ob bereits Reklamationen existieren
      if (!this.order.claims || this.order.claims.length === 0) {
        return "RK-";
      }

      // Finde alle RK-Prefixe die bereits für diese Bestellung existieren
      const existingPrefixes = this.order.claims
        .map(claim => {
          const orderId = claim.orderId;
          // Extrahiere das Prefix (z.B. "RK-", "RK2-", "RK3-")
          const match = orderId.match(/^(RK\d*-)/);
          return match ? match[1] : null;
        })
        .filter(prefix => prefix !== null);

      // Wenn keine RK-Prefixe gefunden wurden, verwende "RK-"
      if (existingPrefixes.length === 0) {
        return "RK-";
      }

      // Finde die höchste Nummer
      let maxNumber = 0;
      existingPrefixes.forEach(prefix => {
        // Extrahiere die Nummer aus dem Prefix (z.B. "2" aus "RK2-")
        const match = prefix.match(/^RK(\d+)-/);
        if (match) {
          const num = parseInt(match[1], 10);
          if (num > maxNumber) {
            maxNumber = num;
          }
        } else if (prefix === "RK-") {
          // "RK-" ohne Nummer entspricht 1
          if (maxNumber === 0) {
            maxNumber = 1;
          }
        }
      });

      // Gib das nächste verfügbare Prefix zurück
      return `RK${maxNumber + 1}-`;
    },

    createBillbeeOrderFromItems(prefix = null) {
      // Wenn kein Prefix angegeben wurde, ermittle automatisch das nächste verfügbare
      if (!prefix) {
        prefix = this.getNextClaimPrefix();
      }

      // Nutze den BillbeeOrderBuilder um eine neue Order zu erstellen
      const builder = new OrderBuilder();

      const originalOrder = this.order.billbeeOrder.Data ? this.order.billbeeOrder.Data : this.order.billbeeOrder;

      // Kombiniere die ausgewählten Items mit den Ersatzteilen
      const combinedItems = [...this.reklaItems];
      
      // Füge Ersatzteile hinzu
      for (const sparePart of this.selectedSpareParts) {
        combinedItems.push({
          Product: {
            SKU: sparePart.sku,
            Title: this.getSparePartName(sparePart.sku),
            Weight: 0
          },
          Quantity: sparePart.quantity,
          TotalPrice: 0,
          UnrebatedTotalPrice: 0,
          Tax: 19,
          TaxAmount: 0,
          Attributes: []
        });
      }

      console.log(originalOrder);

      return builder
        .setOrderNumber(`${prefix}${originalOrder.OrderNumber}`)
        .setState(0) // Neu
        .setVatMode(0)
        .setCreatedAt(new Date().toISOString())
        .setPaymentMethod(0) // Keine Zahlung
        .setShippingCost(0)
        .setTotalCost(0)
        .setCurrency(originalOrder.Currency)
        .setCustomer(originalOrder.Customer)
        .setInvoiceAddress(originalOrder.InvoiceAddress)
        .setShippingAddress(originalOrder.ShippingAddress)
        .setBillbeeShopId(originalOrder.BillbeeShopId)
        .setApiAccountId(originalOrder.ApiAccountId)
        .setApiAccountName(originalOrder.ApiAccountName)
        // Füge ausgewählte Items plus Ersatzteile hinzu mit 0€ Preis
        .setOrderItems(combinedItems.map(item => ({
          ...item,
          TotalPrice: 0,
          UnrebatedTotalPrice: 0
        })))
        // Füge Kommentar hinzu
        .addComment({
          FromCustomer: false,
          Text: `Reklamation zu ${originalOrder.OrderNumber}\nGrund: ${this.reklaData.reason}\nNotiz: ${this.reklaData.note}\nTyp: ${this.reklaData.type}`,
          Created: new Date().toISOString()
        })
        .build();
    },


    async createDHLLabel() {
      this.loading = true;

      let payload = this.dhlData;
      try {
        let response = (await service.createShipment(payload)).data;

        console.log(response);

        let file = atob(response.file);
        const byteNumbers = new Array(file.length);
        for (let i = 0; i < file.length; i++) {
          byteNumbers[i] = file.charCodeAt(i);
        }
        let fileArray = new Uint8Array(byteNumbers);
        let blobfile = new Blob([fileArray], { type: "buffer" });

        console.log(fileArray);
        console.log(blobfile);

        fileDownload(blobfile, "DHLLabel.pdf", "application/pdf");

        this.dhlDialog = false;
      } catch (err) {
        console.error(err);
        this.error = "Es konnte kein Label für diese Adresse erzeugt werden!";
      } finally {
        this.loading = false;
        this.dhlData = {
          orderId: "",
          name: "",
          name2: "",
          street: "",
          streetNumber: "",
          addition: "",
          zip: "",
          city: "",
        };
      }
    },
    async loadDHLDialog(orderId) {
      this.loading = true;

      try {
        let response = (await service.loadOrderData(orderId)).data;
        this.dhlDialog = true;
        this.dhlData.orderId = orderId;
        this.dhlData.name = response.name1;
        this.dhlData.name2 = response.name2 + " " + response.name3;
        this.dhlData.street = response.address1;
        this.dhlData.streetNumber = response.address2;
        this.dhlData.addition = response.address3;
        this.dhlData.zip = response.postalCode;
        this.dhlData.city = response.town;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async processBulkReklamationen() {
      this.processingOrders = true;
      this.processedResults = [];
      this.failedOrderIds = '';
      
      const orderIds = this.bulkOrderIds.split('\n').map(id => id.trim()).filter(id => id);
      this.progressTotal = orderIds.length;
      this.progressValue = 0;
      
      const failedOrders = [];
      
      for (const orderId of orderIds) {
        try {
          // Bestellung laden
          const orderResponse = await service.getMongoOrder(orderId);
          const order = orderResponse.data;

          if (!order) {
            failedOrders.push(orderId);
            this.processedResults.push({
              orderId,
              success: false,
              message: 'Bestellung nicht gefunden'
            });
            continue;
          }


          const originalOrder = order.billbeeOrder.Data ? order.billbeeOrder.Data : order.billbeeOrder;

          // Reklamation erstellen
          const newOrder = this.createBulkOrderWithSpareParts(originalOrder);

          // Erstelle MongoDB-Items aus den ausgewählten Sockeln und Ersatzteilen
          const mongoItems = this.createBulkMongoItems(order.products);

          await service.createRekla({
            originalOrder: originalOrder,
            warrantyOrder: newOrder,
            mongoItems: mongoItems,
            note: this.bulkReklaData.note,
            reason: this.bulkReklaData.reason,
            type: this.bulkReklaData.type
          });

          this.processedResults.push({
            orderId,
            success: true,
            message: 'Reklamation erfolgreich erstellt'
          });

        } catch (error) {
          failedOrders.push(orderId);
          console.error(`Fehler bei Bestellung ${orderId}:`, error);
          this.processedResults.push({
            orderId,
            success: false,
            message: error.message || 'Unbekannter Fehler'
          });
        } finally {
          this.progressValue++;
        }
      }
      
      this.failedOrderIds = failedOrders.join('\n');
      this.processingOrders = false;
    },
    
    // Methode zum Hinzufügen von Ersatzteilen für Bulk-Reklamationen
    addBulkSparePart() {
      if (!this.selectedBulkSparePart || this.bulkSparePartQuantity < 1) return;
      
      // Prüfen, ob das Ersatzteil bereits hinzugefügt wurde
      const existingIndex = this.bulkSelectedSpareParts.findIndex(part => part.sku === this.selectedBulkSparePart.sku);
      
      if (existingIndex !== -1) {
        // Erhöhe nur die Menge, wenn das Teil bereits existiert
        this.bulkSelectedSpareParts[existingIndex].quantity += this.bulkSparePartQuantity;
      } else {
        // Füge neues Ersatzteil hinzu
        const newSparePart = {
          name: this.selectedBulkSparePart.name,
          sku: this.selectedBulkSparePart.sku,
          quantity: this.bulkSparePartQuantity,
          price: 0
        };
        
        this.bulkSelectedSpareParts.push(newSparePart);
      }
      
      // Reset der Eingabefelder
      this.selectedBulkSparePart = null;
      this.bulkSparePartQuantity = 1;
    },
    
    // Methode zum Entfernen von Ersatzteilen für Bulk-Reklamationen
    removeBulkSparePart(item) {
      const index = this.bulkSelectedSpareParts.findIndex(part => part.sku === item.sku);
      if (index !== -1) {
        this.bulkSelectedSpareParts.splice(index, 1);
      }
    },
    
    // Erstellt eine Order für Bulk-Reklamationen mit Ersatzteilen
    createBulkOrderWithSpareParts(originalOrder) {
      const builder = new OrderBuilder();
      
      // Erstelle Order-Items für Ersatzteile
      const sparePartItems = this.bulkSelectedSpareParts.map(sparePart => ({
        Product: {
          SKU: sparePart.sku,
          Title: this.getSparePartName(sparePart.sku),
          Weight: 0
        },
        Quantity: sparePart.quantity,
        TotalPrice: 0,
        UnrebatedTotalPrice: 0,
        Tax: 19,
        TaxAmount: 0,
        Attributes: []
      }));
      
      // Filtere den Holzsockel aus den OrderItems, wenn vorhanden
      const filteredOriginalItems = originalOrder.OrderItems
        .filter(item => item.Product.SKU === 'A10325')
        .map(item => ({
          ...item,
          TotalPrice: 0,
          UnrebatedTotalPrice: 0
        }));
      
      // Kombiniere die gefilterten Items mit den Ersatzteilen
      const combinedItems = [...filteredOriginalItems, ...sparePartItems];
      
      return builder
        .setOrderNumber(`RKCUT-${originalOrder.OrderNumber}`)
        .setState(0)
        .setVatMode(0)
        .setCreatedAt(new Date().toISOString())
        .setPaymentMethod(0)
        .setShippingCost(0)
        .setTotalCost(0)
        .setCurrency(originalOrder.Currency)
        .setCustomer(originalOrder.Customer)
        .setInvoiceAddress(originalOrder.InvoiceAddress)
        .setShippingAddress(originalOrder.ShippingAddress)
        .setBillbeeShopId(originalOrder.BillbeeShopId)
        .setApiAccountId(originalOrder.ApiAccountId)
        .setApiAccountName(originalOrder.ApiAccountName)
        .setOrderItems(combinedItems)
        .addComment({
          FromCustomer: false,
          Text: `Reklamation zu ${originalOrder.OrderNumber}\nGrund: ${this.bulkReklaData.reason}\nNotiz: ${this.bulkReklaData.note}\nTyp: ${this.bulkReklaData.type}`,
          Created: new Date().toISOString()
        })
        .build();
    },
    
    // Erstellt MongoDB-Items für Bulk-Reklamationen
    createBulkMongoItems(originalProducts) {
      // Filtere die mongoItems
      const filteredMongoItems = originalProducts.filter(item => item.attributes && Object.keys(item.attributes).length > 0);
      
      // Füge Ersatzteile zu MongoDB-Items hinzu
      for (const sparePart of this.bulkSelectedSpareParts) {
        filteredMongoItems.push({
          sku: sparePart.sku,
          qty: sparePart.quantity,
          orderItemId: `spare-${sparePart.sku}`,
          attributes: {
            'Ersatzteil': 'Ja',
            'Name': sparePart.name
          }
        });
      }
      
      return filteredMongoItems;
    },
    async loadPrinters() {
      try {
        const response = await service.getPrinters();
        this.printers = response.data;
      } catch (error) {
        console.error('Fehler beim Laden der Drucker:', error);
      }
    },

    async loadReasons(){
      try {
        const response = await service.getWarrantyReasons();
        this.reasons = response.data;
      } catch (error) {
        console.error('Fehler beim Laden der Gründe:', error);
      }
    },
    async openPrinterDialog(shippingId) {
      this.currentShippingId = shippingId;
      await this.loadPrinters();
      this.printerDialog = true;
    },
    async printShippingLabel() {
      if (!this.selectedPrinter || !this.currentShippingId) return;
      
      this.printingLabel = true;
      try {
        await service.printShippingLabel({
          shippingId: this.currentShippingId,
          printer: this.selectedPrinter
        });
        this.printerDialog = false;
        this.$toast.success('Label wurde gedruckt');
      } catch (error) {
        console.error('Fehler beim Drucken:', error);
        this.$toast.error('Fehler beim Drucken des Labels');
      } finally {
        this.printingLabel = false;
      }
    },
    openImage(image) {
      window.open(image, '_blank');
    },
    async addSparePart() {
      if (!this.selectedSparePart || this.sparePartQuantity < 1) return;
      
      // Prüfen, ob das Ersatzteil bereits hinzugefügt wurde
      const existingIndex = this.selectedSpareParts.findIndex(part => part.sku === this.selectedSparePart.sku);
      
      if (existingIndex !== -1) {
        // Erhöhe nur die Menge, wenn das Teil bereits existiert
        this.selectedSpareParts[existingIndex].quantity += this.sparePartQuantity;
      } else {
        // Füge neues Ersatzteil hinzu
        const newSparePart = {
          name: this.selectedSparePart.name,
          sku: this.selectedSparePart.sku,
          quantity: this.sparePartQuantity,
          price: 0
        };
        
        this.selectedSpareParts.push(newSparePart);
      }
      
      // Reset der Eingabefelder
      this.selectedSparePart = null;
      this.sparePartQuantity = 1;
    },
    removeSparePart(item) {
      const index = this.selectedSpareParts.findIndex(part => part.sku === item.sku);
      if (index !== -1) {
        this.selectedSpareParts.splice(index, 1);
      }
    },
    getSparePartName(sku) {
      const sparePart = this.spareParts.find(part => part.sku === sku);
      return sparePart ? sparePart.name : sku;
    },
    // Diese Methode wird vor dem Absenden der Reklamation aufgerufen, um die MongoDB-Items zu aktualisieren
    updateMongoItemsWithSpareParts() {
      // Kopiere die bereits ausgewählten MongoDB-Items
      const updatedMongoItems = [...this.reklaData.mongoItems];
      
      // Füge Ersatzteile zu MongoDB-Items hinzu
      for (const sparePart of this.selectedSpareParts) {
        updatedMongoItems.push({
          sku: sparePart.sku,
          qty: sparePart.quantity,
          orderItemId: `spare-${sparePart.sku}`,
          attributes: {
            'Ersatzteil': 'Ja',
            'Name': sparePart.name
          }
        });
      }
      
      // Aktualisiere die reklaData.mongoItems
      this.reklaData.mongoItems = updatedMongoItems;
    },
    async getMilestones() {
      try {
        const response = await customerSupportService.getMilestones(this.order.orderId);
        this.milestones = response.data;
      } catch (error) {
        console.error('Fehler beim Laden der Meilensteine:', error);
        this.milestones = [];
      }
    },
    getTimelineColor(type) {
      const colorMap = {
        'orderCreated': 'blue',
        'orderProduced': 'purple',
        'orderPacked': 'green',
        'multiCartPlaced': 'orange',
        'multiCartRemoved': 'deep-orange',
        'multiCartConfirmed': 'amber',
        'orderShipped': 'teal',
        'orderDelivered': 'success',
        'orderReturned': 'warning',
        'orderCancelled': 'error',
        'warrantyCreated': 'red',
        'warrantyShipped': 'pink',
        'embroideryLabelPrinted': 'indigo',
        'embroideryScanned': 'cyan',
        'embroideryExported': 'teal'
      };
      return colorMap[type] || 'grey';
    },
    getTimelineIcon(type) {
      const iconMap = {
        'orderCreated': 'mdi-cart-plus',
        'orderPrinted': 'mdi-printer',
        'orderProduced': 'mdi-laser-pointer',
        'multiCartPlaced': 'mdi-archive-arrow-down',
        'multiCartRemoved': 'mdi-archive-arrow-up',
        'multiCartConfirmed': 'mdi-barcode-scan',
        'orderPacked': 'mdi-package-variant',
        'orderShipped': 'mdi-truck-delivery',
        'orderDelivered': 'mdi-check-circle',
        'orderReturned': 'mdi-keyboard-return',
        'orderCancelled': 'mdi-cancel',
        'warrantyCreated': 'mdi-alert-circle',
        'warrantyShipped': 'mdi-truck-fast',
        'embroideryLabelPrinted': 'mdi-printer',
        'embroideryScanned': 'mdi-barcode-scan',
        'embroideryExported': 'mdi-file-export'
      };
      return iconMap[type] || 'mdi-circle';
    },
    formatMilestoneDate(date) {
      if (!date) return '';
      return moment(date).format('dddd, DD.MM.YYYY HH:mm');
    },
    formatMilestoneDateTime(date) {
      if (!date) return '';
      return moment(date).format('dddd, DD.MM.YYYY HH:mm');
    },
    formatMilestoneTime(date) {
      if (!date) return '';
      return moment(date).format('HH:mm');
    },
    getTimelineTitle(type) {
      const titleMap = {
        'orderCreated': 'Bestellung erstellt',
        'orderProduced': 'Produkt produziert',
        'orderPacked': 'Bestellung gepackt',
        'multiCartPlaced': 'Multicart Label gedruckt',
        'multiCartRemoved': 'Produkt aus MultiCart entnommen',
        'multiCartConfirmed': 'MultiCart Label gescannt',
        'orderShipped': 'Produkt versandt',
        'orderDelivered': 'Produkt geliefert',
        'orderReturned': 'Rücksendung',
        'orderCancelled': 'Bestellung storniert',
        'warrantyCreated': 'Garantie erstellt',
        'warrantyShipped': 'Garantie versandt',
        'embroideryLabelPrinted': 'Stickerei Label gedruckt',
        'embroideryScanned': 'Stickerei gescannt',
        'embroideryExported': 'Stickerei exportiert'
      };
      return titleMap[type] || 'Unbekannt';
    },
    getDaysSinceStart(currentDate, index) {
      if (!this.milestones || this.milestones.length === 0 || index === 0) {
        return null;
      }
      
      const firstDate = moment(this.milestones[0].date);
      const current = moment(currentDate);
      const daysDiff = current.diff(firstDate, 'days');
      const hoursDiff = current.diff(firstDate, 'hours');
      
      if (daysDiff === 0) {
        if (hoursDiff === 0) {
          const minutesDiff = current.diff(firstDate, 'minutes');
          return `${minutesDiff} Min.`;
        }
        return `${hoursDiff} Std.`;
      } else if (daysDiff === 1) {
        return '1 Tag';
      } else {
        return `${daysDiff} Tage`;
      }
    },
    getDaysSinceFirstMilestone(currentDate) {
      if (!this.milestones || this.milestones.length === 0) {
        return null;
      }
      
      const firstDate = moment(this.milestones[0].date);
      const current = moment(currentDate);
      const daysDiff = current.diff(firstDate, 'days');
      const hoursDiff = current.diff(firstDate, 'hours');
      
      if (daysDiff === 0) {
        if (hoursDiff === 0) {
          const minutesDiff = current.diff(firstDate, 'minutes');
          return `${minutesDiff} Min.`;
        }
        return `${hoursDiff} Std.`;
      } else if (daysDiff === 1) {
        return '1 Tag';
      } else {
        return `${daysDiff} Tage`;
      }
    },
    getTrackingEventColor(event) {
      // Basierend auf dem statusCode oder Status
      const statusCode = event.statusCode || '';
      const status = (event.status || event.description || '').toLowerCase();
      
      if (statusCode === 'delivered' || status.includes('zugestellt') || status.includes('delivered')) {
        return 'success';
      } else if (statusCode === 'transit' || status.includes('zustellung') || status.includes('delivery') || status.includes('zustellfahrzeug')) {
        return 'orange';
      } else if (status.includes('transport') || status.includes('unterwegs')) {
        return 'blue';
      } else if (status.includes('sortier') || status.includes('sort') || status.includes('briefzentrum')) {
        return 'purple';
      } else if (statusCode === 'pre-transit' || status.includes('angekündigt') || status.includes('announced')) {
        return 'grey';
      } else if (status.includes('abholung') || status.includes('pickup')) {
        return 'teal';
      }
      return 'grey';
    },
    getTrackingEventIcon(event) {
      const statusCode = event.statusCode || '';
      const status = (event.status || event.description || '').toLowerCase();
      
      if (statusCode === 'delivered' || status.includes('zugestellt') || status.includes('delivered')) {
        return 'mdi-check-circle';
      } else if (statusCode === 'transit' || status.includes('zustellung') || status.includes('delivery') || status.includes('zustellfahrzeug')) {
        return 'mdi-home-import-outline';
      } else if (status.includes('transport') || status.includes('unterwegs')) {
        return 'mdi-truck-fast';
      } else if (status.includes('sortier') || status.includes('sort') || status.includes('briefzentrum')) {
        return 'mdi-package-variant-closed';
      } else if (statusCode === 'pre-transit' || status.includes('angekündigt') || status.includes('announced')) {
        return 'mdi-email-outline';
      } else if (status.includes('abholung') || status.includes('pickup')) {
        return 'mdi-package-up';
      }
      return 'mdi-circle-outline';
    },
    formatTrackingDate(date) {
      if (!date) return '';
      return moment(date).format('dddd, DD.MM.YYYY HH:mm');
    },
    formatTrackingTime(date) {
      if (!date) return '';
      return moment(date).format('HH:mm');
    },
    getLocationString(location) {
      if (typeof location === 'string') {
        return location;
      } else if (typeof location === 'object' && location.address) {
        // DHL Format
        if (location.address.addressLocality) {
          return location.address.addressLocality;
        }
      } else if (typeof location === 'object' && location.street && location.city) {
        // Anderes Format
        return `${location.street}, ${location.city}`;
      }
      return '';
    },
  },
  breforeDestroy() { },
  computed: {
    isOldAmazonOrder() {
      if (this.order?.billbeeOrder?.Data) {
        const isAmazon =  this.order.billbeeOrder.Data ? this.order.billbeeOrder.Data.ApiAccountName.toLowerCase().includes('amazon') : this.order.billbeeOrder.ApiAccountName.toLowerCase().includes('amazon')
        const orderDate = new Date(this.order.billbeeOrder.Data.CreatedAt);
        const daysDiff = Math.floor((new Date() - orderDate) / (1000 * 60 * 60 * 24));
        return isAmazon && daysDiff > 40;
      }
      return false;
    }
  },
};
</script>

<style scoped>
.v-card,
.v-btn {
  margin-bottom: 10px;
}

/* Timeline Styles */
.v-timeline-item {
  padding-bottom: 24px;
}

.v-timeline-item:last-child {
  padding-bottom: 0;
}

.v-timeline::before {
  background: #e0e0e0;
  width: 2px;
}

.v-timeline-item__dot {
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

/* Mobile Timeline Cards */
@media (max-width: 960px) {
  .v-card.outlined {
    border-left: 4px solid;
  }
}
</style>